import { useState } from "react";
import { motion } from "framer-motion";
import {
  Star,
  MapPin,
  Phone,
  MessageCircle,
  ChevronRight,
  Award,
  Flame,
  Quote,
  Calendar,
  Mail,
  Instagram,
} from "lucide-react";
import DemoBackBar from "../components/DemoBackBar";
import { restaurant } from "../data/demos";
import { contact } from "../data/contact";

const press = [
  "Time Out",
  "Eater",
  "GQ",
  "Conde Nast Traveler",
  "Bon Appétit",
];

const awards = [
  { year: "2025", text: "Michelin Guide · Recommended" },
  { year: "2024", text: "Best New Restaurant — City Eats" },
  { year: "2024", text: "Top 50 Mediterranean — Eater" },
];

const signatures = [
  {
    img: "https://images.unsplash.com/photo-1544025162-d76694265947?w=900&q=80",
    name: "Wood Fired Lamb",
    note:
      "Twelve hours, hand rubbed in seven spices, finished over olive wood for that quiet smoke."
  },
  {
    img: "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=900&q=80",
    name: "Saffron Risotto",
    note: "Carnaroli, Spanish saffron, lemon zest, finished tableside.",
  },
  {
    img: "https://images.unsplash.com/photo-1559847844-5315695dadae?w=900&q=80",
    name: "Olive Flame Steak",
    note: "Aged 28 days, charred shallot, bone marrow butter.",
  },
];

const formatIls = (value) => `₪${Number(value).toLocaleString("en-IL")}`;

export default function RestaurantDemo() {
  const [activeCat, setActiveCat] = useState(restaurant.menu[0].category);
  const items = restaurant.menu.find((m) => m.category === activeCat)?.items || [];

  const wa = `https://wa.me/${contact.whatsapp.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(
    "Hi Olive Flame, I'd like to place an order."
  )}`;

  return (
    <div className="bg-olive-bg text-white min-h-screen overflow-x-hidden">
      <DemoBackBar accentColor="#FF7A1A" demoName="Olive Flame · Restaurant" />

      <header className="sticky top-[40px] z-40 backdrop-blur-md bg-olive-bg/80 border-b border-white/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-3 sm:py-4 flex items-center justify-between gap-3">
          <a href="#top" className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Flame size={16} className="text-olive-flame flex-shrink-0" />
            <span className="font-serif-display text-xl sm:text-2xl italic text-olive-flame">Olive</span>
            <span className="font-display text-base sm:text-xl font-black tracking-tight text-white">FLAME</span>
          </a>
          <nav className="hidden lg:flex items-center gap-8 text-[15px] font-semibold text-white/70">
            <a href="#story" className="hover:text-olive-flame transition-colors">Story</a>
            <a href="#menu" className="hover:text-olive-flame transition-colors">Menu</a>
            <a href="#signatures" className="hover:text-olive-flame transition-colors">Signatures</a>
            <a href="#events" className="hover:text-olive-flame transition-colors">Events</a>
            <a href="#reviews" className="hover:text-olive-flame transition-colors">Reviews</a>
            <a href="#visit" className="hover:text-olive-flame transition-colors">Visit</a>
          </nav>
          <a
            href="#reserve"
            className="text-[10px] sm:text-xs uppercase tracking-[0.18em] sm:tracking-[0.2em] bg-olive-flame text-black font-bold px-4 sm:px-5 py-2.5 sm:py-3 hover:bg-white transition-colors flex-shrink-0"
            data-testid="restaurant-reserve-top"
          >
            Reserve
          </a>
        </div>
      </header>

      {/* Hero */}
      <section id="top" className="relative min-h-[90vh] sm:h-[92vh] sm:min-h-[600px] overflow-hidden">
        <img
          src={restaurant.hero}
          alt={restaurant.name}
          fetchPriority="high"
          className="absolute inset-0 h-full w-full object-cover scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-black/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-olive-bg via-transparent to-transparent" />
        <div className="relative h-full mx-auto max-w-7xl px-5 sm:px-8 flex flex-col justify-end pt-32 sm:pt-24 pb-16 sm:pb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="inline-flex w-fit items-center gap-2 px-3 py-1.5 rounded-full border border-olive-flame/40 bg-black/40 backdrop-blur-sm"
          >
            <Award size={12} className="text-olive-flame" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-olive-amber">
              Michelin Recommended · 2025
            </span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="mt-5 sm:mt-6 font-serif-display text-[56px] leading-[0.92] sm:text-7xl md:text-8xl lg:text-9xl font-light italic"
          >
            Olive <span className="text-olive-flame">Flame</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-5 sm:mt-6 max-w-xl text-base sm:text-lg text-white/85"
          >
            Wood fired Mediterranean. Quietly serious about fire, time, and seasonal produce.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4 }}
            className="mt-8 sm:mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-3"
          >
            <a
              href="#menu"
              className="bg-olive-flame text-black font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 hover:bg-white transition-colors text-center"
              data-testid="restaurant-cta-order"
            >
              Order Online
            </a>
            <a
              href="#reserve"
              className="border border-white/30 hover:border-olive-flame text-white font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 transition-colors text-center"
              data-testid="restaurant-cta-reserve"
            >
              Reserve a Table
            </a>
            <a
              href={wa}
              target="_blank"
              rel="noreferrer"
              className="border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10 font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 inline-flex items-center justify-center gap-2"
              data-testid="restaurant-whatsapp-order"
            >
              <MessageCircle size={14} /> WhatsApp Order
            </a>
          </motion.div>
        </div>
      </section>

      {/* Press strip */}
      <section className="border-y border-white/5 bg-black/60">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-6 sm:py-8 flex flex-col sm:flex-row items-center sm:justify-between gap-5">
          <span className="text-[10px] uppercase tracking-[0.4em] text-olive-amber">As featured in</span>
          <div className="flex flex-wrap items-center justify-center gap-x-6 sm:gap-x-10 gap-y-3 text-white/55 font-serif-display italic text-base sm:text-lg">
            {press.map((p) => (
              <span key={p}>{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section id="story" className="px-5 sm:px-8 py-20 md:py-28" data-testid="restaurant-story">
        <div className="mx-auto max-w-7xl grid gap-10 md:gap-14 lg:grid-cols-2 items-center">
          <div className="relative aspect-[4/5] overflow-hidden rounded-sm">
            <img
              src="https://images.unsplash.com/photo-1577219491135-ce391730fb2c?w=1200&q=80"
              alt="Chef"
              loading="lazy"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover hover:scale-105 transition-transform duration-[1500ms]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <span className="text-[10px] uppercase tracking-[0.3em] text-olive-amber">Chef · Founder</span>
              <p className="mt-2 font-serif-display text-2xl sm:text-3xl italic">Marco Beneditti</p>
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.4em] text-olive-amber">The Story</div>
            <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl md:text-6xl italic font-light leading-[1.05]">
              Born of fire.<br />
              <span className="text-olive-flame">Built on patience.</span>
            </h2>
            <p className="mt-6 text-white/70 leading-relaxed text-base sm:text-lg">
              Olive Flame began as a single olive wood oven in a small dining room.
              We never built a chain. We built a kitchen — one that respects the
              ingredient, the flame, and the people across the table.
            </p>
            <p className="mt-4 text-white/60 leading-relaxed">
              {restaurant.about}
            </p>
            <div className="mt-10 grid grid-cols-3 gap-4 sm:gap-6 max-w-md">
              <Stat n="12yr" l="Open since" />
              <Stat n="38" l="Seats nightly" />
              <Stat n="4.9★" l="Diner rating" />
            </div>
          </div>
        </div>
      </section>

      {/* Signatures */}
      <section
        id="signatures"
        className="px-5 sm:px-8 py-20 md:py-28 bg-gradient-to-b from-black to-olive-bg"
        data-testid="restaurant-signatures"
      >
        <div className="mx-auto max-w-7xl">
          <div className="text-[11px] uppercase tracking-[0.4em] text-olive-amber">Signatures</div>
          <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl md:text-6xl italic font-light leading-[1.05]">
            Three plates the room comes back for.
          </h2>
          <div className="mt-12 grid gap-5 sm:gap-6 md:grid-cols-3">
            {signatures.map((s, i) => (
              <motion.div
                key={s.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="group relative overflow-hidden rounded-sm"
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={s.img}
                    alt={s.name}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover group-hover:scale-110 transition-transform duration-[1500ms]"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-olive-amber">
                    Signature 0{i + 1}
                  </span>
                  <h3 className="mt-2 font-serif-display text-2xl sm:text-3xl italic">{s.name}</h3>
                  <p className="mt-2 text-sm text-white/75 max-w-xs">{s.note}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Menu */}
      <section id="menu" className="px-5 sm:px-8 py-24 md:py-32" data-testid="restaurant-menu">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:gap-8">
            <div>
              <div className="text-[11px] uppercase tracking-[0.4em] text-olive-amber">Our Menu</div>
              <h2 className="mt-3 font-serif-display text-5xl sm:text-6xl md:text-7xl italic font-light leading-[1.02]">
                Tasted nightly.
              </h2>
            </div>
            <div className="flex flex-wrap gap-2 -mx-1">
              {restaurant.menu.map((c) => (
                <button
                  key={c.category}
                  onClick={() => setActiveCat(c.category)}
                  className={`px-5 sm:px-6 py-3 text-xs sm:text-sm uppercase tracking-[0.2em] sm:tracking-[0.24em] border transition-colors ${
                    activeCat === c.category
                      ? "bg-olive-flame text-black border-olive-flame"
                      : "border-white/15 text-white/70 hover:border-olive-flame hover:text-olive-flame"
                  }`}
                  data-testid={`restaurant-menu-tab-${c.category.toLowerCase()}`}
                >
                  {c.category}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-10 sm:mt-12 grid gap-5 sm:gap-6 md:grid-cols-2">
            {items.map((it, i) => (
              <motion.div
                key={it.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="group flex items-start justify-between gap-5 sm:gap-6 p-5 sm:p-6 border border-white/10 hover:border-olive-flame/60 transition-colors"
                data-testid={`menu-item-${it.name.toLowerCase().replace(/\s/g, "-")}`}
              >
                <div className="min-w-0">
                  <h3 className="font-display text-xl sm:text-2xl font-bold leading-tight">{it.name}</h3>
                  <p className="mt-1.5 text-base text-white/55 leading-relaxed">{it.desc}</p>
                </div>
                <div className="flex flex-col items-end gap-3 shrink-0">
                  <span className="font-display text-2xl sm:text-3xl font-bold text-olive-amber">{formatIls(it.price)}</span>
                  <a
                    href={wa}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[10px] uppercase tracking-[0.25em] text-white/60 hover:text-olive-flame inline-flex items-center gap-1"
                  >
                    Order <ChevronRight size={12} />
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Awards */}
      <section className="px-5 sm:px-8 py-16 sm:py-20 bg-black border-y border-white/5">
        <div className="mx-auto max-w-7xl">
          <div className="text-[11px] uppercase tracking-[0.4em] text-olive-amber text-center">Recognition</div>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {awards.map((a, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="flex items-start gap-5 border-l-2 border-olive-flame/40 pl-6 py-2"
              >
                <div>
                  <div className="font-mono-tech text-olive-flame text-xs">{a.year}</div>
                  <p className="mt-2 font-serif-display italic text-xl text-white/85">{a.text}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Private Events */}
      <section
        id="events"
        className="relative px-5 sm:px-8 py-20 md:py-28 overflow-hidden"
        data-testid="restaurant-events"
      >
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1800&q=80"
            alt=""
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-olive-bg via-olive-bg/85 to-transparent" />
        </div>
        <div className="mx-auto max-w-7xl">
          <div className="max-w-xl">
            <div className="text-[11px] uppercase tracking-[0.4em] text-olive-amber">Private Events</div>
            <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl md:text-6xl italic font-light leading-[1.05]">
              Buy out the room.
            </h2>
            <p className="mt-5 text-white/70 leading-relaxed text-base sm:text-lg">
              Anniversaries, brand dinners, intimate weddings. We host private
              dinners for 12 to 38 with a custom-printed menu and a sommelier
              walk-through.
            </p>
            <a
              href="#reserve"
              className="mt-8 inline-block bg-olive-flame text-black font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 hover:bg-white transition-colors"
            >
              Plan an event
            </a>
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section id="reviews" className="px-5 sm:px-8 py-20 md:py-28" data-testid="restaurant-reviews">
        <div className="mx-auto max-w-7xl">
          <div className="text-[11px] uppercase tracking-[0.4em] text-olive-amber">Guest Notes</div>
          <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl md:text-6xl italic font-light leading-[1.05]">
            Loved by locals.
          </h2>
          <div className="mt-12 grid gap-5 sm:gap-6 md:grid-cols-3">
            {restaurant.reviews.map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="border border-white/10 p-6 sm:p-7 hover:border-olive-flame/40 transition-colors relative"
              >
                <Quote size={22} className="text-olive-flame/50" />
                <div className="mt-3 flex gap-1 text-olive-flame">
                  {Array.from({ length: r.stars }).map((_, k) => (
                    <Star key={k} size={13} fill="currentColor" />
                  ))}
                </div>
                <p className="mt-4 text-white/80 leading-relaxed">"{r.quote}"</p>
                <p className="mt-5 text-xs uppercase tracking-[0.25em] text-white/45">— {r.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reserve / Visit */}
      <section
        id="reserve"
        className="px-5 sm:px-8 py-20 md:py-28 bg-gradient-to-b from-black to-olive-bg"
        data-testid="restaurant-reserve"
      >
        <div className="mx-auto max-w-7xl grid gap-10 lg:grid-cols-2">
          <div id="visit">
            <div className="text-[11px] uppercase tracking-[0.4em] text-olive-amber">Visit</div>
            <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl italic font-light">Find us.</h2>
            <ul className="mt-8 space-y-4 text-white/85">
              <li className="flex items-center gap-3 text-sm sm:text-base">
                <MapPin size={16} className="text-olive-flame flex-shrink-0" /> {restaurant.address}
              </li>
              <li className="flex items-center gap-3 text-sm sm:text-base">
                <Phone size={16} className="text-olive-flame flex-shrink-0" /> {restaurant.phone}
              </li>
              <li className="flex items-center gap-3 text-sm sm:text-base">
                <Instagram size={16} className="text-olive-flame flex-shrink-0" /> @oliveflame
              </li>
            </ul>
            <h3 className="mt-10 text-[11px] uppercase tracking-[0.4em] text-olive-amber">Hours</h3>
            <ul className="mt-4 space-y-2 text-white/75 font-mono-tech text-xs sm:text-sm">
              {restaurant.hours.map((h) => (
                <li key={h.day} className="flex justify-between max-w-xs border-b border-white/5 py-2">
                  <span>{h.day}</span>
                  <span>{h.time}</span>
                </li>
              ))}
            </ul>
          </div>
          <ReservationForm wa={wa} />
        </div>
      </section>

      {/* Newsletter */}
      <section className="px-5 sm:px-8 py-16 bg-olive-bg border-t border-white/5">
        <div className="mx-auto max-w-3xl text-center">
          <Mail size={22} className="text-olive-flame mx-auto" />
          <h3 className="mt-4 font-serif-display text-3xl sm:text-4xl italic font-light">Quiet updates only.</h3>
          <p className="mt-3 text-white/60 max-w-md mx-auto text-sm sm:text-base">
            New menu drops, private events, and seasonal nights. One email per
            month. No spam, ever.
          </p>
          <form
            onSubmit={(e) => e.preventDefault()}
            className="mt-7 flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <input
              type="email"
              required
              placeholder="you@example.com"
              className="flex-1 bg-white/5 border border-white/10 px-5 py-3 text-sm focus:border-olive-flame focus:outline-none"
            />
            <button
              type="submit"
              className="bg-olive-flame text-black text-xs uppercase tracking-[0.2em] font-bold px-6 py-3 hover:bg-white transition-colors"
            >
              Join
            </button>
          </form>
        </div>
      </section>

      <footer className="border-t border-white/5 px-5 sm:px-8 py-10 text-center text-xs text-white/40">
        © {new Date().getFullYear()} Olive Flame Restaurant — Demo by Salama Solutions
      </footer>
    </div>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-display text-2xl sm:text-3xl font-black text-olive-flame">{n}</div>
      <div className="mt-1 text-[10px] uppercase tracking-[0.25em] text-white/50">{l}</div>
    </div>
  );
}

function ReservationForm({ wa }) {
  const [done, setDone] = useState(false);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setDone(true);
      }}
      className="border border-white/10 p-6 sm:p-8 md:p-10 bg-black/40 rounded-sm"
      data-testid="restaurant-reserve-form"
    >
      <div className="text-[11px] uppercase tracking-[0.4em] text-olive-amber">Reserve</div>
      <h3 className="mt-3 font-serif-display text-2xl sm:text-3xl italic font-light">Book a table.</h3>
      {done ? (
        <div
          className="mt-8 text-white/85 text-sm leading-relaxed"
          data-testid="restaurant-reserve-success"
        >
          Thanks — your reservation request has been received. The host will
          confirm shortly.{" "}
          <a href={wa} target="_blank" rel="noreferrer" className="text-olive-flame underline">
            Or message us on WhatsApp
          </a>
          .
        </div>
      ) : (
        <div className="mt-6 grid gap-4">
          <input required placeholder="Full name" className="restaurant-input" data-testid="reserve-name" />
          <input required type="email" placeholder="Email" className="restaurant-input" />
          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <input required type="date" className="restaurant-input" data-testid="reserve-date" />
            <input required type="time" className="restaurant-input" data-testid="reserve-time" />
          </div>
          <input required type="number" min="1" placeholder="Guests" className="restaurant-input" data-testid="reserve-guests" />
          <textarea rows={3} placeholder="Anything special? Allergies, occasion…" className="restaurant-input resize-none" />
          <button
            type="submit"
            className="mt-2 bg-olive-flame text-black font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 hover:bg-white transition-colors inline-flex items-center justify-center gap-2"
            data-testid="reserve-submit"
          >
            <Calendar size={14} /> Request Reservation
          </button>
        </div>
      )}
      <style>{`
        .restaurant-input {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.1);
          color: #fff;
          padding: 14px 16px;
          font-size: 14px;
          outline: none;
          width: 100%;
        }
        .restaurant-input:focus { border-color: #FF7A1A; }
        .restaurant-input::placeholder { color: rgba(255,255,255,0.35); }
      `}</style>
    </form>
  );
}
